import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { ordersApi, addressesApi } from '../lib/api';
import AddressManager from '../components/AddressManager';

export const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogleSession, loginWithPhone } = useAuth();
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [googleBusy, setGoogleBusy] = useState(false);
  // Phone OTP state
  const [phone, setPhone] = useState('+91');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Handle Emergent-managed Google OAuth callback (session_id in URL hash).
  useEffect(() => {
    if (!location.hash?.includes('session_id=')) return;
    const params = new URLSearchParams(location.hash.replace(/^#/, ''));
    const sid = params.get('session_id');
    if (!sid) return;
    setGoogleBusy(true);
    loginWithGoogleSession(sid)
      .then(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate('/account', { replace: true });
      })
      .catch((e) => {
        setErr(e?.response?.data?.detail || 'Google sign-in failed');
        window.history.replaceState({}, document.title, window.location.pathname);
      })
      .finally(() => setGoogleBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
    }
    return window.recaptchaVerifier;
  };

  const resetRecaptcha = () => {
    try { window.recaptchaVerifier?.clear(); } catch (_) {}
    window.recaptchaVerifier = null;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setErr('');
    if (!/^\+[1-9]\d{7,14}$/.test(phone)) {
      setErr('Enter phone in international format, e.g. +919876543210');
      return;
    }
    setPhoneBusy(true);
    try {
      const verifier = setupRecaptcha();
      const conf = await signInWithPhoneNumber(auth, phone, verifier);
      setConfirmation(conf);
      setOtpSent(true);
    } catch (e2) {
      resetRecaptcha();
      setErr(e2?.message || 'Failed to send OTP. Please try again.');
    } finally { setPhoneBusy(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setErr(''); setPhoneBusy(true);
    try {
      const cred = await confirmation.confirm(otp.trim());
      const idToken = await cred.user.getIdToken();
      await loginWithPhone(idToken, name || null);
      navigate('/account');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || 'Invalid or expired code.');
    } finally { setPhoneBusy(false); }
  };

  return (
    <main className="bg-[#f5f1e6] min-h-screen text-[#1a1410] flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-md">
        <img
          src="/velouva-logo.jpg"
          alt="VELOUVA Cosmetic"
          className="h-20 w-20 object-contain -ml-2 mb-2"
          style={{ mixBlendMode: 'multiply' }}
        />
        <h1 className="font-serif text-[48px] leading-[1.05] mt-6 italic">Welcome.</h1>
        <p className="mt-3 text-[#5a4a3e]">Sign in with your phone number or Google account.</p>

        {err && <div className="mt-6 p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}

        <div className="mt-8 space-y-6">
          {!otpSent ? (
            <form onSubmit={handleSendOtp} className="space-y-6">
              <div>
                <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">NAME (OPTIONAL)</label>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="phone-name-input" className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">PHONE NUMBER</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+919876543210"
                  data-testid="phone-input"
                  className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none"
                />
                <p className="mt-2 text-[11px] text-[#7a6a5a]">Include your country code, e.g. +91 for India.</p>
              </div>
              <button type="submit" disabled={phoneBusy} data-testid="send-otp-btn" className="w-full bg-[#1a1410] text-[#f5f1e6] py-4 text-[11px] tracking-[0.32em] hover:bg-black transition-colors disabled:opacity-50">
                {phoneBusy ? 'SENDING…' : 'SEND OTP'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div>
                <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">ENTER OTP</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="6-digit code"
                  data-testid="otp-input"
                  className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none tracking-[0.3em]"
                />
                <p className="mt-2 text-[11px] text-[#7a6a5a]">Sent to {phone}.</p>
              </div>
              <button type="submit" disabled={phoneBusy} data-testid="verify-otp-btn" className="w-full bg-[#1a1410] text-[#f5f1e6] py-4 text-[11px] tracking-[0.32em] hover:bg-black transition-colors disabled:opacity-50">
                {phoneBusy ? 'VERIFYING…' : 'VERIFY & CONTINUE'}
              </button>
              <button type="button" onClick={() => { setOtpSent(false); setOtp(''); setConfirmation(null); resetRecaptcha(); }} data-testid="otp-change-number-btn" className="w-full text-[11px] tracking-[0.28em] text-[#7a6a5a] hover:text-[#1a1410]">
                CHANGE NUMBER
              </button>
            </form>
          )}
          <div id="recaptcha-container" />
        </div>

        <div className="mt-6 flex items-center gap-3 text-[10px] tracking-[0.28em] text-[#7a6a5a]">
          <span className="h-px flex-1 bg-[#cdc2b1]" />
          OR
          <span className="h-px flex-1 bg-[#cdc2b1]" />
        </div>

        <button
          type="button"
          onClick={handleGoogle}
          disabled={googleBusy}
          data-testid="google-signin-btn"
          className="mt-6 w-full flex items-center justify-center gap-3 bg-white border border-[#1a1410] text-[#1a1410] py-4 text-[11px] tracking-[0.32em] hover:bg-[#1a1410] hover:text-[#f5f1e6] transition-colors disabled:opacity-50"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          {googleBusy ? 'SIGNING IN…' : 'CONTINUE WITH GOOGLE'}
        </button>

        <div className="mt-8">
          <Link to="/" className="text-[11px] tracking-[0.28em] text-[#7a6a5a] hover:text-[#1a1410]">RETURN TO COLLECTION</Link>
        </div>
      </div>
    </main>
  );
};

// Hidden admin sign-in (email/password). Not linked anywhere in the UI.
export const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u?.is_admin ? '/admin' : '/account');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Invalid credentials');
    } finally { setBusy(false); }
  };

  return (
    <main className="bg-[#f5f1e6] min-h-screen text-[#1a1410] flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-md">
        <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block">VELOUVA · ADMIN</div>
        <h1 className="font-serif text-[48px] leading-[1.05] mt-6 italic">Admin sign in.</h1>
        <p className="mt-3 text-[#5a4a3e]">Restricted access.</p>

        {err && <div className="mt-6 p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div>
            <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">EMAIL</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email-input" className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">PASSWORD</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password-input" className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
          </div>
          <button type="submit" disabled={busy} data-testid="admin-submit-btn" className="w-full bg-[#1a1410] text-[#f5f1e6] py-4 text-[11px] tracking-[0.32em] hover:bg-black transition-colors disabled:opacity-50">
            {busy ? '…' : 'SIGN IN'}
          </button>
        </form>

        <div className="mt-8">
          <Link to="/" className="text-[11px] tracking-[0.28em] text-[#7a6a5a] hover:text-[#1a1410]">RETURN TO MAISON</Link>
        </div>
      </div>
    </main>
  );
};

export const Account = () => {
  const { user, logout, loading, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  const [form, setForm] = useState({ name: '', address: '', city: '', postal_code: '', country: '' });
  const [defaultAddrId, setDefaultAddrId] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');

  const loadAddresses = async () => {
    try {
      const list = await addressesApi.list();
      const def = list.find((a) => a.is_default) || list[0] || null;
      setDefaultAddrId(def?.id || null);
      setForm((f) => ({
        ...f,
        address: def?.address || '',
        city: def?.city || '',
        postal_code: def?.postal_code || '',
        country: def?.country || '',
      }));
    } catch (_) {}
  };

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: user.name || '' }));
      ordersApi.list().then(setOrders).catch(() => setOrders([]));
      loadAddresses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true); setProfileMsg(''); setProfileErr('');
    try {
      const name = form.name.trim();
      if (!name) throw new Error('Name cannot be empty');
      if (name !== user.name) await updateProfile({ name });

      const addrPayload = {
        label: 'Home',
        full_name: name,
        address: form.address.trim(),
        city: form.city.trim(),
        postal_code: form.postal_code.trim(),
        country: form.country.trim(),
        is_default: true,
      };
      const hasAddr = addrPayload.address || addrPayload.city || addrPayload.postal_code || addrPayload.country;
      if (defaultAddrId) {
        await addressesApi.update(defaultAddrId, addrPayload);
      } else if (hasAddr) {
        const created = await addressesApi.create(addrPayload);
        setDefaultAddrId(created.id);
      }
      await loadAddresses();
      setProfileMsg('Profile updated.');
    } catch (e2) {
      setProfileErr(e2?.response?.data?.detail || e2?.message || 'Update failed');
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return <main className="bg-[#f5f1e6] min-h-screen px-6 py-20 text-center text-[#7a6a5a]">Loading…</main>;
  }

  if (!user) {
    return (
      <main className="bg-[#f5f1e6] min-h-screen text-[#1a1410] flex items-center justify-center px-6 py-20">
        <div className="text-center">
          <h1 className="font-serif text-[40px] italic">You&apos;re not signed in.</h1>
          <p className="mt-3 text-[#5a4a3e]">Sign in to access your maison account.</p>
          <Link to="/login" className="mt-8 inline-block bg-[#1a1410] text-[#f5f1e6] px-8 py-4 text-[11px] tracking-[0.32em]">SIGN IN</Link>
        </div>
      </main>
    );
  }

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main className="bg-[#f5f1e6] min-h-screen text-[#1a1410] px-6 lg:px-10 py-20">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between gap-4">
          <h1 className="font-serif text-[40px] sm:text-[56px] leading-[1.05]">Welcome, <span className="italic">{user.name}.</span></h1>
          <button
            onClick={() => { logout(); navigate('/'); }}
            data-testid="signout-btn"
            className="shrink-0 mt-2 inline-flex items-center gap-2 border border-[#1a1410] px-5 py-2.5 text-[11px] tracking-[0.28em] hover:bg-[#1a1410] hover:text-[#f5f1e6] transition-colors"
          >
            SIGN OUT
          </button>
        </div>

        {/* Editable Profile */}
        <form onSubmit={saveProfile} className="mt-12 border border-[#cdc2b1] p-8" data-testid="profile-form">
          <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block">PROFILE</div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <div>
              <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">NAME</label>
              <input value={form.name} onChange={setField('name')} required data-testid="profile-name-input"
                className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">PHONE {user.phone ? '(VERIFIED)' : ''}</label>
              <input
                value={user.phone || user.email || 'Not linked'}
                readOnly
                disabled
                data-testid="profile-phone-readonly"
                className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] py-2 text-[#5a4a3e] cursor-not-allowed focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">ADDRESS</label>
              <input value={form.address} onChange={setField('address')} data-testid="profile-address-input"
                className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">CITY</label>
              <input value={form.city} onChange={setField('city')} data-testid="profile-city-input"
                className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">POSTAL / PIN CODE</label>
              <input value={form.postal_code} onChange={setField('postal_code')} data-testid="profile-postal-input"
                className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">COUNTRY</label>
              <input value={form.country} onChange={setField('country')} data-testid="profile-country-input"
                className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
            </div>
          </div>

          {profileErr && <div className="mt-5 p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{profileErr}</div>}
          {profileMsg && <div className="mt-5 p-3 bg-[#e6efe1] border border-[#b7ceac] text-sm text-[#3d5a2a]" data-testid="profile-success">{profileMsg}</div>}

          <div className="mt-6 flex items-center gap-4">
            <button type="submit" disabled={savingProfile} data-testid="profile-save-btn"
              className="bg-[#1a1410] text-[#f5f1e6] px-8 py-3 text-[11px] tracking-[0.32em] hover:bg-black transition-colors disabled:opacity-50">
              {savingProfile ? 'SAVING…' : 'SAVE CHANGES'}
            </button>
            <div className="text-[11px] tracking-[0.22em] text-[#7a6a5a]">{orders.length} {orders.length === 1 ? 'ORDER' : 'ORDERS'} · <Link to="/shop" className="border-b border-[#1a1410] pb-0.5">BROWSE THE COLLECTIONS</Link></div>
          </div>
        </form>

        {orders.length > 0 && (
          <div className="mt-14">
            <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block mb-6">ORDER HISTORY</div>
            <div className="divide-y divide-[#e5dfd0]">
              {orders.map((o) => {
                const isOnline = o.payment?.provider === 'razorpay' || o.payment?.provider === 'online';
                return (
                  <div key={o.id} className="py-6">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <div className="font-serif text-lg">Order {o.id.slice(0, 8).toUpperCase()}</div>
                        <div className="text-xs text-[#7a6a5a] flex flex-wrap items-center gap-2 mt-1">
                          <span>{new Date(o.created_at).toLocaleDateString()}</span>
                          <span>·</span>
                          <span>{o.items.length} item{o.items.length !== 1 ? 's' : ''}</span>
                          <span>·</span>
                          <span className="uppercase tracking-[0.15em]">{o.status}</span>
                          <span
                            data-testid={`account-order-payment-${o.id}`}
                            className={`ml-1 px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border ${
                              isOnline
                                ? 'bg-[#e6efe1] border-[#b7ceac] text-[#3d5a2a]'
                                : 'bg-[#f3e9d8] border-[#d8c69a] text-[#7a5a1a]'
                            }`}
                          >
                            {isOnline ? 'Online' : 'COD'}
                          </span>
                        </div>
                      </div>
                      <div className="font-medium text-lg tabular-nums">Rs. {o.total.toFixed(2)}</div>
                    </div>
                    <ul className="mt-4 space-y-3" data-testid={`order-items-${o.id}`}>
                      {o.items.map((it, idx) => (
                        <li key={idx} className="flex items-center gap-4" data-testid={`order-item-${o.id}-${idx}`}>
                          {it.product_id ? (
                            <Link to={`/product/${it.product_id}`} className="shrink-0 group">
                              {it.image ? (
                                <img
                                  src={it.image}
                                  alt={it.name}
                                  className="w-16 h-20 object-cover bg-[#e9e2d3] transition-opacity group-hover:opacity-80"
                                  loading="lazy"
                                  data-testid={`order-item-image-${o.id}-${idx}`}
                                />
                              ) : (
                                <span className="block w-16 h-20 bg-[#e9e2d3]" />
                              )}
                            </Link>
                          ) : null}
                          <div className="min-w-0">
                            {it.product_id ? (
                              <Link
                                to={`/product/${it.product_id}`}
                                className="font-serif text-lg leading-snug hover:underline underline-offset-4"
                                data-testid={`order-item-name-${o.id}-${idx}`}
                              >
                                {it.name}
                              </Link>
                            ) : (
                              <span className="font-serif text-lg leading-snug">{it.name}</span>
                            )}
                            {it.shade ? <div className="text-xs tracking-[0.18em] text-[#7a6a5a] uppercase mt-0.5">{it.shade}</div> : null}
                            <div className="text-xs text-[#5a4a3e] mt-1 tabular-nums">{it.quantity} × Rs. {Number(it.price).toFixed(2)}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-14">
          <AddressManager onChange={loadAddresses} />
        </div>
      </div>
    </main>
  );
};
